import React, { useCallback, useMemo } from 'react';
import { range } from 'ramda';
import { EDStickyTable } from '../../StickyTable/EDStickyTable';
import { EDStickyTableHeader } from '../../StickyTable/Components/EDStickyTableHeader';
import { EDStickyTableHeaderStickyCell } from '../../StickyTable/Components/EDStickyTableHeaderStickyCell';
import { EDStickyTableHeaderCell } from '../../StickyTable/Components/EDStickyTableHeaderCell';
import { EDStickyTableRows } from '../../StickyTable/Components/EDStickyTableRows';
import { EDStickyTableRow } from '../../StickyTable/Components/EDStickyTableRow';
import { EDStickyTableStickyCell } from '../../StickyTable/Components/EDStickyTableStickyCell';
import {
  EDStickyTableCell,
  EDStickyTableCellSelectedEvent,
  EDStickyTableCellSelectionClickEvent,
} from '../../StickyTable/Components/EDStickyTableCell';
import {
  incDay,
  getDate,
  firstDayInWeek,
  incMin,
} from '../../../Utils/dateUtils';
import {
  IBooking,
  ICalendarViewProps,
  prepareViewBookings,
  timeRangeOverlap,
  updateSelection,
} from '../Utils/EDCalendarViewUtils';
import { dateToStr } from '../../../Utils/stringUtils';
import { EDCalendarViewWeekNumberBox } from './EDCalendarViewWeekNumberBox';
import { EDCalendarViewDayCell } from './EDCalendarViewDayCell';
import { EDCalendarViewResourceNameCell } from './EDCalendarViewResourceNameCell';
import { EDCalendarViewDayViewBookings } from './EDCalendarViewDayViewBookings';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';

const HEAD_HEIGHT = 50; // Anpassad höjd för rubrikrad
const DAY_WIDTH = 120;
const MIN_RES_WIDTH = 100; // Minimal bredd per resurskolumn
const MIN_DAY_HEIGHT = 90; // Minimal höjd för datumrad
const MIN_DAY_HEIGHT_MOBILE = 60;
const DAY_WIDTH_MOBILE = 50;

export const EDCalendarViewDayView: React.FC<ICalendarViewProps> = props => {
  const isSmallScreen = useIsSmallScreen();
  const resCount = props.resources.length;
  const dayCount = props.dayCount ?? 7;
  const tableHeight = Math.max(props.height - (props.bottomInsetPx ?? 0), 0);
  const viewStartTime = useMemo(
    () =>
      dayCount > 1
        ? firstDayInWeek(getDate(props.currentDate))
        : getDate(props.currentDate),
    [props.currentDate, dayCount]
  );
  const viewEndTime = useMemo(
    () => incDay(viewStartTime, dayCount),
    [viewStartTime, dayCount]
  );

  // Hantera bokningar
  const bookingColumns = useMemo(
    () =>
      prepareViewBookings(
        props.bookings,
        viewStartTime,
        viewEndTime,
        booking =>
          booking.resourceId
            ? `${dateToStr(booking.startTime)}_${booking.resourceId}`
            : undefined,
        false
      ),
    [props.bookings, viewStartTime, viewEndTime]
  );

  // Hantera markering
  const selStart = useMemo(() => props.selStart, [props.selStart]);
  const selEnd = useMemo(() => props.selEnd, [props.selEnd]);
  const selResources = useMemo(() => props.selResources, [props.selResources]);
  const allResIds = useMemo(
    () => props.resources.map(res => res.id),
    [props.resources]
  );

  const onSelectionChange = props.onSelectionChange;
  const onSelectionClick = props.onSelectionClick;
  const onBookingClick = props.onBookingClick;

  const handleCellSelected: EDStickyTableCellSelectedEvent = useCallback(
    (
      res: number,
      day: number,
      shift: boolean,
      ctrl: boolean,
      drag: boolean
    ) => {
      const cellStartTime = incDay(viewStartTime, day);
      const resId = allResIds[res];
      if (onSelectionChange) {
        const resSelected = (selResources ?? []).some(selId => selId === resId);
        let newResIds = [...(selResources ?? [])];
        if (ctrl) {
          if (!resSelected) {
            newResIds.push(resId);
          }
        } else if (
          (shift || drag) &&
          selResources &&
          selResources.length > 0 &&
          allResIds.length > 0
        ) {
          const idxFr = allResIds.indexOf(selResources[0]);
          const idxTo = allResIds.indexOf(resId);
          newResIds =
            idxFr === idxTo
              ? [resId]
              : [
                  selResources[0],
                  ...allResIds.slice(
                    Math.min(idxFr, idxTo) + 1,
                    Math.max(idxFr, idxTo)
                  ),
                  resId,
                ];
        } else {
          newResIds = [resId];
        }

        if ((shift || drag) && selStart && selEnd) {
          updateSelection(
            selStart,
            cellStartTime,
            newResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        } else {
          updateSelection(
            cellStartTime,
            cellStartTime,
            newResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        }
      }
    },
    [
      viewStartTime,
      allResIds,
      selStart,
      selEnd,
      selResources,
      onSelectionChange,
    ]
  );

  const handleSelectionClick: EDStickyTableCellSelectionClickEvent =
    useCallback(
      ref => {
        if (onSelectionClick) {
          onSelectionClick(ref, selStart, selEnd, selResources);
        }
      },
      [selStart, selEnd, selResources, onSelectionClick]
    );

  return (
    <EDStickyTable
      width={props.width}
      height={tableHeight}
      colCount={resCount + 1}
      rowCount={dayCount + 1}
      minColWidth={MIN_RES_WIDTH}
      minRowHeight={isSmallScreen ? MIN_DAY_HEIGHT_MOBILE : MIN_DAY_HEIGHT}
      firstColWidth={isSmallScreen ? DAY_WIDTH_MOBILE : DAY_WIDTH}
      firstRowHeight={HEAD_HEIGHT}
      firstColSticky
      firstRowSticky
      firstCellAlignment="center"
      firstColAlignment="center"
      firstColVerticalAlignment="center"
      firstRowTextAlignment="center"
    >
      <EDStickyTableHeader>
        <EDStickyTableHeaderStickyCell>
          <EDCalendarViewWeekNumberBox date={viewStartTime} />
        </EDStickyTableHeaderStickyCell>
        {range(0, resCount).map(res => (
          // Titelrad med resurser
          <EDStickyTableHeaderCell key={res}>
            <EDCalendarViewResourceNameCell
              resourceName={props.resources[res].name}
              isSmallScreen={isSmallScreen}
            />
          </EDStickyTableHeaderCell>
        ))}
      </EDStickyTableHeader>
      <EDStickyTableRows>
        {range(0, dayCount).map(day => {
          // Dagrader
          const d = incDay(viewStartTime, day);
          return (
            <EDStickyTableRow key={day}>
              <EDStickyTableStickyCell>
                <EDCalendarViewDayCell
                  day={d}
                  dayWidth={DAY_WIDTH}
                  isSmallScreen={isSmallScreen}
                />
              </EDStickyTableStickyCell>
              {range(0, resCount).map(res => {
                // Kalenderceller med bokningar
                const resId = props.resources[res].id;
                const key = `${dateToStr(d)}_${resId}`;
                const bookings: IBooking[] = bookingColumns[key];
                const cellStartTime = d;
                const cellEndTime = incDay(d, 1);
                const resSelected =
                  props.selResources && props.selResources.indexOf(resId) >= 0;
                const selected =
                  resSelected &&
                  props.selStart &&
                  props.selEnd &&
                  timeRangeOverlap(
                    incMin(props.selStart, 15),
                    incMin(props.selEnd, 15),
                    cellStartTime,
                    cellEndTime
                  );
                return (
                  <EDStickyTableCell
                    key={res}
                    col={res}
                    row={day}
                    selected={selected}
                    onSelectionClick={handleSelectionClick}
                    onCellSelected={handleCellSelected}
                  >
                    <EDCalendarViewDayViewBookings
                      bookings={bookings}
                      onBookingClick={onBookingClick}
                      view="All"
                    />
                  </EDStickyTableCell>
                );
              })}
            </EDStickyTableRow>
          );
        })}
      </EDStickyTableRows>
    </EDStickyTable>
  );
};
